<template>
  <div v-if="detail">
    <section class="hero-slab">
      <div class="hero-slab__media" :style="{ backgroundImage: 'url(' + detail.heroImage + ')' }"></div>
      <div class="hero-slab__overlay"></div>
      <div class="hero-slab__content container reveal">
        <div class="section-title">
          <p>{{ detail.eyebrow }}</p>
          <h1>{{ detail.title }}</h1>
        </div>
        <p>{{ detail.intro }}</p>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.products'), to: '/products' },
        { label: $t('nav.colours'), to: '/colours' },
        { label: detail.title, to: $route.path }
      ]"
      :back-label="$t('common.backToColours')"
      back-to="/colours"
    />

    <section class="section-spacing light-panel">
      <div class="container grid-two reveal">
        <div class="section-title">
          <p>How it reads</p>
          <h2>The design logic behind this colour family</h2>
        </div>
        <ul class="detail-list detail-list--dark">
          <li v-for="item in detail.principles" :key="item">{{ item }}</li>
        </ul>
      </div>
    </section>

    <section class="section-spacing dark-panel">
      <div class="container reveal">
        <div class="section-title section-title--light colour-detail-title">
          <p>Colour specification</p>
          <h2>Decisions that change the final read</h2>
        </div>
        <div class="colour-spec-grid">
          <article v-for="item in detail.specNotes" :key="item.label">
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </article>
        </div>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container reveal">
        <div class="colour-logic-grid">
          <article v-for="item in detail.finishLogic" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section-spacing dark-panel">
      <div class="container reveal">
        <div class="section-title section-title--light colour-detail-title">
          <p>Species and finish cues</p>
          <h2>Material directions to sample</h2>
        </div>
        <div class="species-grid">
          <article v-for="item in detail.species" :key="item.name" class="species-card">
            <h3>{{ item.name }}</h3>
            <p>{{ item.note }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container grid-two reveal">
        <div class="section-title">
          <p>Pairs well with</p>
          <h2>Interiors this palette tends to support</h2>
        </div>
        <ul class="detail-list detail-list--dark">
          <li v-for="item in detail.pairings" :key="item">{{ item }}</li>
        </ul>
      </div>
    </section>

    <section class="section-spacing dark-panel">
      <div class="container reveal">
        <div class="section-title section-title--light colour-detail-title">
          <p>{{ $t('caseDetail.gallery') }}</p>
          <h2>How this colour family reads in context</h2>
        </div>
        <div class="gallery-grid">
          <div v-for="image in detail.gallery" :key="image" class="gallery-grid__item">
            <img :src="image" :alt="detail.title" loading="lazy">
          </div>
        </div>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container colour-detail-cta reveal">
        <div class="section-title">
          <p>Build this direction</p>
          <h2>Pair colour with product format and sample approval</h2>
        </div>
        <div>
          <router-link to="/products" class="colour-detail-cta__link">{{ $t('caseDetail.viewProducts') }}</router-link>
          <router-link to="/customize" class="colour-detail-cta__link colour-detail-cta__link--dark">Customize this finish</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'

export default {
  name: 'ColourDetailPage',
  components: {
    PageTrail
  },
  computed: {
    detail () {
      return site.colourDetails[this.$route.params.slug] || null
    }
  },
  watch: {
    '$route.params.slug' () {
      if (!this.detail) {
        this.$router.replace('/colours')
      }
    }
  },
  mounted () {
    if (!this.detail) {
      this.$router.replace('/colours')
    }
  }
}
</script>

<style>
.species-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

.colour-detail-title {
  margin-bottom: 32px;
}

.colour-spec-grid,
.colour-logic-grid {
  display: grid;
  gap: 18px;
}

.colour-spec-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.colour-spec-grid article {
  padding: 22px;
  background: #fff;
  color: var(--dark-text);
}

.colour-spec-grid span,
.colour-spec-grid strong {
  display: block;
}

.colour-spec-grid span {
  margin-bottom: 8px;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.48);
}

.colour-spec-grid strong {
  font-size: 21px;
  font-weight: 400;
  line-height: 1.35;
}

.colour-logic-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.colour-logic-grid article {
  padding: 28px;
  border: 1px solid rgba(23, 20, 17, 0.12);
  background: #fff;
}

.colour-logic-grid h3 {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 400;
}

.colour-logic-grid p {
  margin: 0;
  line-height: 1.8;
  color: rgba(23, 20, 17, 0.76);
}

.species-card {
  padding: 28px;
  background: #fff;
  color: var(--dark-text);
}

.species-card h3 {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 400;
}

.species-card p {
  margin: 0;
  line-height: 1.8;
}

.colour-detail-cta {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.colour-detail-cta__link {
  min-width: 148px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  border: 1px solid rgba(23, 20, 17, 0.16);
}

.colour-detail-cta__link--dark {
  background: #171411;
  color: #f5f3ef;
}

@media (max-width: 1100px) {
  .species-grid,
  .colour-spec-grid,
  .colour-logic-grid {
    grid-template-columns: 1fr;
  }

  .colour-detail-cta {
    align-items: flex-start;
    flex-direction: column;
  }

  .colour-detail-cta__link {
    margin: 0 10px 10px 0;
  }
}
</style>
